/**
 * Deciding whether a 401 means "your session ended" or something else.
 *
 * Sessions expire after 20 minutes of inactivity, so this fires several times a
 * day for an ordinary user — it has to be right about the two cases where a 401
 * is not an expiry:
 *
 *  - POST /api/auth/login — a wrong password is a 401. Treating it as expiry
 *    would clear the session state out from under the login form and compete
 *    with its own "invalid email or password" message.
 *  - GET /api/auth/me — 401 is the *normal* answer for somebody who is not
 *    signed in, which AuthContext already maps to null deliberately. Treating
 *    it as expiry would also fire for every anonymous reader of the public
 *    documentation, who is entitled to be anonymous.
 *
 * 403 is deliberately not included: that is "signed in, not allowed here" — a
 * viewer reaching an admin route. The session is fine, and sending them to the
 * login page would be a lie about what went wrong.
 */
const NOT_EXPIRY = ["/api/auth/login", "/api/auth/me"];

export function isSessionExpiry(url: string, status: number): boolean {
  if (status !== 401) return false;
  // Compare on the path alone: callers pass relative URLs today, but a query
  // string ("/api/auth/me?x=1") or an absolute one must not slip past the
  // exclusions, and a path that merely *contains* one of them
  // ("/api/auth/me-something-else") must not be caught by them.
  const path = new URL(url, "http://local").pathname;
  return !NOT_EXPIRY.includes(path);
}

type Handler = () => void;
let handler: Handler | null = null;

/**
 * Registered once by AuthProvider. Lives at module scope because apiFetch is
 * plain TypeScript with no access to React context, and routing the signal
 * through every call site instead would mean every caller could forget it.
 *
 * Returns an unsubscribe so a remount (or a test) cannot leave a stale closure
 * holding a dead query client.
 */
export function onSessionExpiry(fn: Handler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

export function notifySessionExpiry(): void {
  handler?.();
}

/**
 * How long the server says the current session has left, in seconds, taken from
 * the X-Session-Expires-In header on every authenticated response.
 *
 * This exists because a tab that is merely open makes no requests: nothing in
 * this app polls, and refetchOnWindowFocus is off globally. Without it the
 * client could not notice a session ending and kept rendering a signed-in UI
 * for a session the server had already rejected — the reader only found out by
 * clicking something.
 *
 * Polling the server instead would have been actively wrong: the API slides the
 * session on every authenticated request, so a keep-alive poll would hold it
 * open forever and cancel the idle timeout.
 *
 * A duration, never a timestamp: the client waits that long rather than
 * comparing clocks, so a browser whose clock disagrees with the server's cannot
 * sign the reader out early or leave them stale.
 */
type DeadlineHandler = (secondsRemaining: number) => void;
let deadlineHandler: DeadlineHandler | null = null;

export function onSessionDeadline(fn: DeadlineHandler): () => void {
  deadlineHandler = fn;
  return () => {
    if (deadlineHandler === fn) deadlineHandler = null;
  };
}

export function notifySessionDeadline(secondsRemaining: number): void {
  deadlineHandler?.(secondsRemaining);
}

/** The header name, so the producer and consumer cannot drift apart. */
export const SESSION_DEADLINE_HEADER = "X-Session-Expires-In";

/**
 * Reads the deadline off a response, or null when there is not a usable one.
 * A malformed value is discarded rather than coerced: NaN would schedule a
 * timer that fires immediately and sign the reader out on the spot.
 */
export function deadlineFromResponse(res: Response): number | null {
  const raw = res.headers.get(SESSION_DEADLINE_HEADER);
  if (raw === null) return null;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}
